import { Children, isValidElement, createContext, useContext, useMemo, type ReactNode } from 'react';
import {
  stepperRoot,
  stepWrapper,
  stepConnector,
  stepConnectorActive,
  stepInner,
  stepCircle,
  stepLabel as stepLabelClass,
} from './stepper.css';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const StepperContext = createContext<{ activeStep: number; stepCount: number }>({
  activeStep: 0,
  stepCount: 0,
});

const StepContext = createContext<{ index: number }>({ index: 0 });

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------

export interface StepperProps {
  activeStep: number;
  children: ReactNode;
  style?: React.CSSProperties;
}

export function Stepper({ activeStep, children, style }: StepperProps) {
  const steps = Children.toArray(children).filter(isValidElement);
  const stepCount = steps.length;

  // Memo the Provider value so consumers don't re-render purely on identity churn.
  const ctxValue = useMemo(() => ({ activeStep, stepCount }), [activeStep, stepCount]);

  return (
    <StepperContext.Provider value={ctxValue}>
      <div className={stepperRoot} style={style} aria-label="Progress steps">
        {steps.map((step, index) => (
          <div key={index} className={stepWrapper}>
            {index > 0 && (
              <div className={`${stepConnector}${index <= activeStep ? ` ${stepConnectorActive}` : ''}`} />
            )}
            <StepIndexProvider index={index}>{step}</StepIndexProvider>
          </div>
        ))}
      </div>
    </StepperContext.Provider>
  );
}

/** Memoizes the per-step StepContext value so each step's consumers see a stable reference. */
function StepIndexProvider({ index, children }: { index: number; children: ReactNode }) {
  const value = useMemo(() => ({ index }), [index]);
  return <StepContext.Provider value={value}>{children}</StepContext.Provider>;
}

// ---------------------------------------------------------------------------
// Step
// ---------------------------------------------------------------------------

export interface StepProps {
  children: ReactNode;
}

export function Step({ children }: StepProps) {
  return (
    <div className={stepInner}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StepLabel
// ---------------------------------------------------------------------------

export interface StepLabelProps {
  children: ReactNode;
}

export function StepLabel({ children }: StepLabelProps) {
  const { activeStep } = useContext(StepperContext);
  const { index } = useContext(StepContext);

  const state = index < activeStep ? 'completed' : index === activeStep ? 'active' : 'pending';
  const stepNumber = index + 1;

  // F14: completed steps showed only the literal "✓" glyph with no
  // accessible name — screen readers heard nothing about completion.
  // Attach data-completed + aria-label so AT users know each completed
  // step's status.
  const ariaLabel =
    state === 'completed' ? `Step ${stepNumber}: completed` :
    state === 'active'    ? `Step ${stepNumber}: current`   :
                            `Step ${stepNumber}`;

  return (
    <>
      <div
        className={stepCircle({ state })}
        aria-current={state === 'active' ? 'step' : undefined}
        aria-label={ariaLabel}
        data-completed={state === 'completed' ? 'true' : undefined}
      >
        {state === 'completed' ? '✓' : stepNumber}
      </div>
      <div className={stepLabelClass({ state })}>
        {children}
      </div>
    </>
  );
}
